import type Phaser from "phaser";
import type { PairData } from "../types";
import type Catboy from "./Catboy";
import type MonsterGirl from "./MonsterGirl";

/**
 * Пара кетбой+демонетка с общим HP. Хранит ссылки на оба спрайта и
 * предоставляет синхронные реакции (hurt-flash, faint-escape).
 */
export default class BattlePair {
  public hp: number;
  public readonly maxHp: number;
  /** Одноразовый бафф уворота (0..1). Сбрасывается при первом hit-check. */
  public evadeBuff = 0;

  constructor(
    private scene: Phaser.Scene,
    public readonly data: PairData,
    public readonly catboy: Catboy,
    public readonly girl: MonsterGirl,
  ) {
    this.maxHp = data.maxHp;
    this.hp = data.maxHp;
  }

  takeDamage(amount: number): number {
    const prev = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    return prev - this.hp;
  }

  /** Восстановить HP, не превышая maxHp. Возвращает фактически отлеченное. */
  heal(amount: number): number {
    const prev = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    return this.hp - prev;
  }

  consumeEvade(): number {
    const v = this.evadeBuff;
    this.evadeBuff = 0;
    return v;
  }

  isFainted(): boolean {
    return this.hp <= 0;
  }

  /** Синхронный hurt-flash обоих спрайтов: 3 раза мигнуть #ff5555 по 100мс. */
  playHurt(): Promise<void> {
    return new Promise((resolve) => {
      const targets = [this.catboy.sprite, this.girl.sprite];
      const FLASH = 0xff5555;
      const RESET = 0xffffff;
      let count = 0;
      const t = this.scene.time.addEvent({
        delay: 100,
        repeat: 5,
        callback: () => {
          count++;
          const tinted = count % 2 === 1;
          targets.forEach((s) => s.setTint(tinted ? FLASH : RESET));
        },
      });
      this.scene.time.delayedCall(620, () => {
        t.remove();
        targets.forEach((s) => s.clearTint());
        resolve();
      });
    });
  }

  /** После hurt-flash проиграть позу смущения (blush) на обеих фигурах. */
  playBlushReact(): Promise<void> {
    return Promise.all([this.catboy.playBlush(), this.girl.playBlush()]).then(() => undefined);
  }

  /**
   * Faint+escape: красный тинт → прикрытие → разворот → убегают за край экрана.
   * direction: куда убегать по X (например, +1 для правого края, -1 для левого).
   */
  playFaintEscape(direction: 1 | -1, screenWidth: number): Promise<void> {
    return new Promise((resolve) => {
      const targets = [this.catboy.sprite, this.girl.sprite];
      targets.forEach((s) => s.setTint(0xff3333));

      this.catboy.playWorry();
      this.girl.stopIdle();
      this.girl.sprite.setTexture(this.girl.pair.girlKeys.blush1);

      this.scene.time.delayedCall(500, () => {
        this.catboy.startRun();
        this.girl.startRun();

        const targetX = direction > 0 ? screenWidth + 80 : -80;
        this.scene.tweens.add({
          targets,
          x: targetX,
          duration: 1000,
          ease: "Quad.easeIn",
          onComplete: () => resolve(),
        });
      });
    });
  }

  destroy(): void {
    this.catboy.sprite.destroy();
    this.girl.sprite.destroy();
  }
}
